import { BorderBox, Box, FlexBox, List, Rhythm, Text } from '@trmediaab/zebra';
import { cloudinary } from '@trmediaab/zebra-utils';
import { history } from 'redux-first-router';

import Paywall from '~/containers/Auth/Paywall';
import { useImportScript } from '~/hooks/useImportScript';
import { getTeaserMedia } from '~/utils/articleUtils';

import Link from '../Link';

const Comments = ({ article }) => {
  // Docs from https://ifragasatt.com/publisher/support/comment/implementation/
  //
  // Mandatory:
  //   customerId – The publisher’s customerId, provided by your Ifrågasätt sales representative.
  //   articleId – A unique id for the article.
  // Recommended:
  //   commentsToShow – Specifies the maximum number of comments visible when the widget is first loaded (e.g. 5).
  //   alias – Determines if readers should be able to use an alias on the article in question. This cannot be change after the first comment is posted in the specific article. True or false.
  //   articleUrl – The URL to the article/post, e.g. https://www.ifragasatt.se/article/post.
  //   Note: if your URL contains accented characters (e.g. “å”, “ø”, “ü”), you need to wrap it in the encodeURI function. Example: articleUrl = encodeURI(“https://ifragasatt.com/article/post/“).
  //   articleTitle – The article/post title. Maximum 255 chars.
  //   articleDescription – A description/preamble for the article/post. Maximum 512 chars.
  //   articleImage – A URL to an image for the article, e.g. https://www.ifragasatt.se/images/article.jpg.
  //   articleTags – Comma separated list of tags for the article/post, e.g. Sport,Football,Manchester. Maximum 255 char.
  //   articlePublishedTime – A timestamp for when the article/post was published, e.g. 2019-01-02 13:44:55
  // Optional:
  //   cssUrl – A URL to a CSS file for styling the commenting widget.
  //   adFrequency – How often ads should be shown in between the comments, (e.g. 5). See the Ads section of this document.
  //   display – Can be set to null or count. If display is set to count, a minimized version of the widget will be loaded. See the Display mode section of this document.
  //   stickylogin – If set to true, a first-party cookie will be on the publisher’s domain, in addition to the Ifrågasätt third-party cookie.

  const articleImage = getTeaserMedia(article);

  const commentsAPI = `${import.meta.env.VITE_COMMENTS_API_HOST}/load?${[
    ['articleId', article.id],
    ['customerId', import.meta.env.VITE_COMMENTS_API_CUSTOMER_ID],
    ['commentsToShow', import.meta.env.VITE_COMMENTS_TO_SHOW],
    [
      'alias',
      import.meta.env.VITE_COMMENTS_ALLOW_ALIASES === 'false' ? false : true,
    ],
    [
      'articleUrl',
      import.meta.env.VITE_CANONICAL_ROOT + history().location.pathname,
    ],
    ['articleTitle', article.headline],
    ['articleDescription', article.preamble],
    [
      'articleImage',
      articleImage != null
        ? cloudinary.getUrl(
            articleImage.id,
            600,
            [16, 9],
            cloudinary.getFocusGravity(articleImage.focus),
          )
        : undefined,
    ],
    ['articleTags', article.tags?.map(({ name }) => name).join(',')],
    ['articlePublishedTime', article.publish_start],
  ]
    .filter(([_, value]) => Boolean(value))
    .map(([name, value]) => `${name}=${encodeURIComponent(value)}`)
    .join('&')}`;

  // Import the comments script from ifragasatt.com
  useImportScript(commentsAPI, true);

  return (
    <>
      <Box mt="2" id={`ifragasatt-${article.id}`} />
    </>
  );
};

const CommentsWrapper = props => {
  const noAccess =
    props.article.comments === CommentsWrapper.OPEN_SUB && !props.hasPremium;

  return (
    <BorderBox borderTop="1" pt="2" mt="2">
      <Rhythm color="text.faded" between="2" mb="2">
        <FlexBox alignItems="center">
          <Text.p fontWeight="semiBold">Kommentera artikeln</Text.p>
        </FlexBox>
        {noAccess && <Paywall isArticle small />}
        {!noAccess && (
          <>
            <Disclaimer />
            <Comments {...props} />
          </>
        )}
      </Rhythm>
    </BorderBox>
  );
};

CommentsWrapper.NONE = 0; // Inga kommentarer
CommentsWrapper.OPEN_SUB = 1; // Öppet för prenumeranter
CommentsWrapper.OPEN_ALL = 2; // Öppet för alla

export default CommentsWrapper;

const Disclaimer = () => (
  <>
    <Text.p fontSize="1">
      I samarbete med Ifrågasätt Media Sverige AB:s (”Ifrågasätt”) tjänst
      Ifrågasätt erbjuder Travronden Spel möjligheten för läsare att kommentera
      artiklar. Denna tjänst tillhandahålls således av Ifrågasätt som också är
      ansvarig för tjänsten. Travronden granskar inte kommentarerna i förväg.
      Kommentarerna omfattas inte av utgivaransvaret enligt
      yttrandefrihetsgrundlagen och de är inte heller en del av den
      grundlagsskyddade databasen{' '}
      <Link to={{ type: 'HOME' }}>TravrondenSpel.se</Link>.
    </Text.p>
    <Text.p fontSize="1">Grundreglerna för kommentarer är:</Text.p>
    <List.bulletList>
      <List.listItem fontSize="1">Håll dig till ämnet</List.listItem>
      <List.listItem fontSize="1">Håll god ton</List.listItem>
      <List.listItem fontSize="1">
        Visa respekt för berörda personer
      </List.listItem>
    </List.bulletList>
    <Text.p fontSize="1">
      I övrigt gäller de regler för kommentarer som framgår av Ifrågasätts
      användarvillkor och som du godkänner i samband med att du skapar ditt
      konto. Ifrågasätt förbehåller sig rätten att radera kommentarer i
      efterhand. Travronden Spel kan genom eget beslut ta bort kommentarer.
    </Text.p>
  </>
);
